import React, { PropsWithChildren, ReactNode } from 'react';
import clsx from 'clsx';
import { Colors, ColorsType } from 'works/legacy/lib/style-helper';

type Props = PropsWithChildren<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
  weight?: 'light' | 'medium' | 'bold';
  color?: ColorsType;
  className?: string;
  transform?: 'capitalize' | 'lowercase' | 'uppercase';
  align?: 'left' | 'center' | 'right';
  italic?: boolean;
  deleted?: boolean;
  secondary?: boolean;
  bold?: boolean; // alias for weight="bold"
  block?: boolean;
  underline?: boolean;
  info?: string;
}>;

export function Text(props: Props) {
  const {
    size = 'md',
    weight = 'light',
    color = 'textDefault',
    transform,
    align,
    className,
    children,
    italic,
    deleted,
    secondary,
    bold,
    block,
    underline,
    info,
  } = props;

  const finalColor = secondary ? 'midnightLighter' : color;
  const colorHash = Colors[finalColor];
  const finalWeight = bold ? 'bold' : weight;
  const internalClassName = clsx(
    className,
    `wk-text wk-text-size--${size} wk-text-weight--${finalWeight}`,
    transform && `wk-text-transform--${transform}`,
    align && `wk-text-align--${align}`,
    {
      'wk-text--italic': italic,
      'wk-text--deleted': deleted,
      'wk-text--underline': underline,
      'wk-text--block': block || !!align,
    }
  );

  return (
    <>
      <span className={internalClassName} title={info}>
        {applyPropInheritanceInTextChildren(children, props)}
      </span>
      <style jsx>{`
        .wk-text {
          color: ${colorHash};
          margin: 0;
        }

        .wk-text--italic {
          font-style: italic;
        }

        .wk-text-weight--light {
          font-weight: 300;
        }
        .wk-text-weight--medium {
          font-weight: 500;
        }
        .wk-text-weight--bold {
          font-weight: 600;
        }

        span.wk-text-size--sm {
          font-size: 12px;
          line-height: 16px;
        }
        span.wk-text-size--md {
          font-size: 14px;
          line-height: 20px;
        }
        span.wk-text-size--lg {
          font-size: 16px;
          line-height: 22px;
        }
        span.wk-text-size--xl {
          font-size: 20px;
          line-height: 24px;
        }

        .wk-text-transform--capitalize {
          text-transform: capitalize;
        }
        .wk-text-transform--lowercase {
          text-transform: lowercase;
        }
        .wk-text-transform--uppercase {
          text-transform: uppercase;
        }

        .wk-text--block {
          display: block;
        }

        .wk-text-align--left {
          text-align: left;
        }
        .wk-text-align--center {
          text-align: center;
        }
        .wk-text-align--right {
          text-align: right;
        }

        .wk-text--deleted {
          text-decoration: line-through;
        }

        .wk-text--underline {
          text-decoration: underline;
          text-decoration-color: ${colorHash};
        }
      `}</style>
    </>
  );
}

function applyPropInheritanceInTextChildren(
  children: ReactNode,
  parentTextProps: Props
): ReactNode {
  return React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Text) {
      const { children: _, ...parentInheritable } = parentTextProps;

      return React.cloneElement(child, {
        ...parentInheritable,
        ...child.props,
      });
    }
    return child;
  });
}
